import { useDispatch } from 'react-redux';
import { setUserInfo } from '../redux/authSlice';
import Cookies from 'universal-cookie';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { userRoleTypes } from '../utils/userRoleTypes';
import { useSetAtom } from 'jotai';
import { userInfoAtom } from '../data/atoms/utilsAtoms';
import JSCookies from 'js-cookie';
import api from '../data/api';
import { setAuthToken } from '../data/utils/axiosConfigInstance';
import { useSWRConfig } from 'swr';
import localStorageKeys from '@/constants/localStorageKeys';
import { buildRoutes } from '@/service/buildRoutes';
import { getStringFromBrowser, removeDataFromBrowser } from '@/utils/browserStorage';
import { useResetAllAtoms } from './useResetAllAtoms';

export const useAuthService = () => {
	const resetAllAtoms = useResetAllAtoms();
	const cookies = new Cookies();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const setUserData = useSetAtom(userInfoAtom);
	const { mutate } = useSWRConfig();

	const loggedInUser = async (userData, fromRegister = false, options = {}) => {
		const { skipRedirect = false } = options;
		const { role, data, userId, token } = userData;
		const baseProfile = data || userData || {};

		if (![userRoleTypes.USER, userRoleTypes.ADMIN].includes(role)) {
			toast.error('User Type is Invalid');
			return;
		}

		const { firstname, lastname } = baseProfile;
		setAuthToken(token);

		let userDetailsData = null;
		try {
			userDetailsData = await api.getPersonalInformation(userId);
			console.log('[useAuthService] User details fetched:', userDetailsData?.data);
		} catch (error) {
			console.error('[useAuthService] Failed to fetch user details:', error);
			userDetailsData = null;
		}

		let secondaryEmailData = null;
		try {
			secondaryEmailData = await api.getSecondaryEmails();
		} catch (error) {
			secondaryEmailData = null;
		}

		const effectiveEmail =
			secondaryEmailData?.data?.secondaryEmail || userDetailsData?.data?.email || userData.email;
		
		// Normalize user data structure to always have user.firstname/lastname
		const normalizedUserData = {
			...userData,
			image: userDetailsData?.data?.image,
			firstname: userDetailsData?.data?.firstname || firstname,
			lastname: userDetailsData?.data?.lastname || lastname,
			email: effectiveEmail,
			userType: userDetailsData?.data?.userType || userData.userType,
			onboardingRole: userDetailsData?.data?.onboardingRole || userData.onboardingRole,
			subscription: userDetailsData?.data?.subscription !== undefined ? userDetailsData?.data?.subscription : userData.subscription,
			subscriptionReferenceId: userDetailsData?.data?.subscriptionReferenceId || userData.subscriptionReferenceId,
			requiredSeats: userDetailsData?.data?.requiredSeats || userData.requiredSeats,
			availableSeats: userDetailsData?.data?.availableSeats || userData.availableSeats,
			onboardingCompleted: userDetailsData?.data?.onboardingCompleted !== undefined ? userDetailsData?.data?.onboardingCompleted : userData.onboardingCompleted
		};

		console.log('[useAuthService] Normalized user data:', normalizedUserData);

		localStorage.setItem('userData', JSON.stringify(normalizedUserData));
		localStorage.setItem('token', token);
		localStorage.setItem('role', role);

		cookies.set('user', normalizedUserData);
		cookies.set('token', token);
		JSCookies.set('token', token, { expires: 90000000 });

		dispatch(setUserInfo(normalizedUserData));

		setUserData({ token, user: normalizedUserData, role });

		console.log('[useAuthService] skipRedirect:', skipRedirect);
		if (skipRedirect) {
			console.log('[useAuthService] Skipping redirect');
			return;
		}

		if (fromRegister) {
			const autoSubscribeWithPlan = localStorage.getItem(localStorageKeys.AUTO_SUBSCRIBE_WITH_PLAN);
			if (autoSubscribeWithPlan) navigate('/plans?autoSubscribe=true');
			else navigate('/');
			return;
		}
		if (role === userRoleTypes.ADMIN) {
			navigate(buildRoutes.toAdminAllVideos());
		} else {
			const redirectPath = getStringFromBrowser(localStorageKeys.REDIRECT_AFTER_LOGIN);
			if (redirectPath) {
				removeDataFromBrowser(localStorageKeys.REDIRECT_AFTER_LOGIN);
				navigate(redirectPath);
			} else {
				navigate('/');
			}
		}
	};

	const clearCookies = async() => {
		const cookies = await cookieStore.getAll();
    	for (const cookie of cookies) {
        await cookieStore.delete(cookie.name);
    	}
	}

	const loggedOutUser = async(redirectToOtherUrl) => {
		JSCookies.remove('token');
		cookies.remove('user');
		dispatch(setUserInfo(null));
		localStorage.removeItem('userData');
		localStorage.removeItem('user');
		localStorage.removeItem('role');
		localStorage.removeItem('token');
		localStorage.removeItem('subscriptionId');
		localStorage.clear();
		sessionStorage.clear();
		await clearCookies();
		resetAllAtoms();
		dispatch(setUserInfo(null));
		setAuthToken(null);

		mutate(key => true, undefined, { revalidate: false });

		setUserData({
			token: null,
			user: null,
			role: null,
			subscriptionId: null
		});

		navigate(redirectToOtherUrl ? redirectToOtherUrl : '/login');
	};

	return {
		loggedInUser,
		loggedOutUser
	};
};
